import { Bot, Pencil, Sparkles, Store, Wallet, Gift, Package, AlertTriangle, CalendarClock, ShieldCheck, User } from 'lucide-react';

const VERIFIED_BADGE = 'https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/mxyyg0c2_verified.png';

const TIER = {
  Platinum: { ar: 'عضو بلاتيني', en: 'Platinum', cls: 'from-slate-300 to-slate-100 text-slate-900' },
  Gold:     { ar: 'عضو ذهبي',    en: 'Gold',     cls: 'from-[#F5A962] to-[#F59E0B] text-white' },
  Silver:   { ar: 'عضو فضي',     en: 'Silver',   cls: 'from-slate-400 to-slate-300 text-slate-900' },
  Blue:     { ar: 'عضو أزرق',    en: 'Blue',     cls: 'from-[#1E9BD9] to-[#38BDF8] text-white' },
};

function StatTile({ icon: Icon, label, value, accent, onClick, dark, testId, delay }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative text-start rounded-2xl p-4 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 cgm-rise ${dark ? 'cgm-glass-dark hover:border-[#1E9BD9]/50' : 'cgm-glass-light hover:border-[#1E9BD9]/60 shadow-sm'}`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={testId}
    >
      <div className="absolute -top-8 -end-8 w-24 h-24 rounded-full blur-2xl opacity-25 transition-opacity group-hover:opacity-50" style={{ background: accent }} />
      <div className="relative flex items-center justify-between mb-2">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}22` }}>
          <Icon className="h-4.5 w-4.5" style={{ color: accent, width: 18, height: 18 }} />
        </span>
      </div>
      <p className={`relative text-2xl font-black tabular-nums leading-none ${dark ? 'text-white' : 'text-[#0F172A]'}`}>{value}</p>
      <p className={`relative text-[11px] font-medium mt-1.5 ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
    </button>
  );
}

function AlertChip({ icon: Icon, children, tone, onClick, testId }) {
  const tones = {
    amber: 'bg-[#F59E0B]/12 text-[#F59E0B] border-[#F59E0B]/30 hover:bg-[#F59E0B]/20',
    red: 'bg-red-500/12 text-red-500 border-red-500/30 hover:bg-red-500/20',
    sky: 'bg-[#1E9BD9]/12 text-[#1E9BD9] border-[#1E9BD9]/30 hover:bg-[#1E9BD9]/20',
  };
  return (
    <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-semibold transition-colors ${tones[tone]}`} data-testid={testId}>
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

/**
 * DashboardHero — Cyber-Luxe account header: avatar ring, greeting, membership,
 * quick stats (ads / wallet / loyalty / orders), AI shortcut and smart alerts.
 */
export default function DashboardHero({ user, isAr, isDarkMode, stats, alerts, onAskAI, onGo, onEditProfile }) {
  const dark = isDarkMode;
  const tier = TIER[user.membership_tier] || TIER.Blue;
  const textMain = dark ? 'text-white' : 'text-[#0F172A]';
  const textMuted = dark ? 'text-slate-400' : 'text-slate-600';
  const initials = (user.name || '?').trim().charAt(0).toUpperCase();

  return (
    <section className="relative overflow-hidden" data-testid="dashboard-hero">
      <div className={`absolute inset-0 ${dark ? 'bg-[#0A0B10]' : 'bg-gradient-to-b from-white to-[#F8FAFC]'}`} />
      <div className={`absolute inset-0 ${dark ? 'cgm-dot-grid opacity-40' : 'cgm-dot-grid-light opacity-50'} [mask-image:radial-gradient(ellipse_at_top,black_25%,transparent_75%)]`} />
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute -top-32 start-[0%] w-[460px] h-[460px] rounded-full blur-[130px] ${dark ? 'bg-[#1E9BD9]/20' : 'bg-[#1E9BD9]/15'}`} />
        <div className={`absolute -bottom-40 end-[4%] w-[380px] h-[380px] rounded-full blur-[120px] ${dark ? 'bg-[#F5A962]/15' : 'bg-[#F5A962]/20'}`} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-10 sm:pb-12">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 flex items-start gap-4 sm:gap-6 animate-fadeIn">
            <div className="relative shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full p-[3px] bg-[conic-gradient(from_180deg,#1E9BD9,#38BDF8,#F5A962,#1E9BD9)] shadow-[0_0_30px_rgba(30,155,217,0.35)]">
                <div className={`w-full h-full rounded-full flex items-center justify-center text-2xl sm:text-3xl font-black ${dark ? 'bg-[#0A0B10] text-white' : 'bg-white text-[#0F172A]'}`}>
                  {initials || <User className="h-10 w-10" />}
                </div>
              </div>
              {user.is_verified && (
                <img src={VERIFIED_BADGE} alt="Verified" className="absolute -bottom-1 -end-1 w-8 h-8 drop-shadow" title={isAr ? 'حساب موثق' : 'Verified Account'} />
              )}
            </div>

            <div className="min-w-0">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border mb-2 text-[11px] font-semibold ${dark ? 'bg-[#1E9BD9]/10 border-[#1E9BD9]/30 text-[#38BDF8]' : 'bg-white border-[#1E9BD9]/30 text-[#0284C7] shadow-sm'}`}>
                <Sparkles className="h-3 w-3" />
                {isAr ? 'لوحة التحكم الذكية' : 'Smart dashboard'}
              </div>
              <h1 className={`text-2xl sm:text-4xl font-extrabold tracking-tight leading-tight truncate ${textMain}`} data-testid="dashboard-title">
                {isAr ? 'مرحباً، ' : 'Welcome, '}<span className="cgm-brand-text">{user.name}</span>
              </h1>
              <p className={`text-sm mt-1 truncate ${textMuted}`}>{user.email}</p>

              <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="membership-inline">
                <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold bg-gradient-to-r ${tier.cls}`}>{isAr ? tier.ar : tier.en}</span>
                {user.membership_number && (
                  <span className={`text-xs font-mono tracking-[0.2em] px-2.5 py-1 rounded-full border ${dark ? 'border-white/10 text-slate-300 bg-white/5' : 'border-slate-200 text-slate-600 bg-white'}`} data-testid="membership-number">
                    {user.membership_number}
                  </span>
                )}
                {user.is_verified ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-500"><ShieldCheck className="h-3.5 w-3.5" />{isAr ? 'موثّق' : 'Verified'}</span>
                ) : null}
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  type="button"
                  onClick={onAskAI}
                  className="h-10 px-4 rounded-xl text-white text-sm font-bold inline-flex items-center gap-2 bg-gradient-to-r from-[#1E9BD9] via-[#38BDF8] to-[#F5A962] hover:shadow-[0_8px_24px_rgba(30,155,217,0.45)] hover:-translate-y-0.5 transition-all"
                  data-testid="dashboard-ask-ai"
                >
                  <Bot className="h-4 w-4" />
                  {isAr ? 'اسأل CGM AI عن حسابي' : 'Ask CGM AI about my account'}
                </button>
                <button
                  type="button"
                  onClick={onEditProfile}
                  className={`h-10 px-4 rounded-xl text-sm font-semibold inline-flex items-center gap-2 border transition-colors ${dark ? 'border-[#1E9BD9]/25 text-white hover:bg-[#1E9BD9]/10' : 'border-[#1E9BD9]/30 text-slate-700 bg-white hover:bg-[#1E9BD9]/5'}`}
                  data-testid="dashboard-edit-profile"
                >
                  <Pencil className="h-4 w-4" />
                  {isAr ? 'تعديل الملف' : 'Edit profile'}
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-3" data-testid="dashboard-stats">
            <StatTile icon={Store} accent="#1E9BD9" dark={dark} delay={100} testId="stat-ads" value={stats.ads} label={isAr ? 'إعلاناتي النشطة' : 'Active ads'} onClick={() => onGo('myads')} />
            <StatTile icon={Wallet} accent="#F5A962" dark={dark} delay={180} testId="stat-wallet" value={stats.wallet} label={isAr ? 'رصيد المحفظة (AED)' : 'Wallet (AED)'} onClick={() => onGo('wallet')} />
            <StatTile icon={Gift} accent="#10B981" dark={dark} delay={260} testId="stat-points" value={stats.points} label={isAr ? 'نقاط الولاء' : 'Loyalty points'} onClick={() => onGo('loyalty')} />
            <StatTile icon={Package} accent="#8B5CF6" dark={dark} delay={340} testId="stat-orders" value={stats.orders} label={isAr ? 'طلباتي' : 'My orders'} onClick={() => onGo('shipments')} />
          </div>
        </div>

        {alerts.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6 animate-fadeIn" data-testid="dashboard-alerts">
            {alerts.map((a) => (
              <AlertChip key={a.id} icon={a.icon === 'pay' ? AlertTriangle : a.icon === 'expire' ? CalendarClock : ShieldCheck} tone={a.tone} onClick={a.onClick} testId={`alert-${a.id}`}>
                {a.label}
              </AlertChip>
            ))}
          </div>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 cgm-scanline opacity-60" />
    </section>
  );
}
